/**
 * OWA-1315: court time zones.
 *
 * A Docket's startTime and endTime are wall-clock times of day in the court's own zone
 * (Postgres TIME, no zone, no date). A Task's startTime is an absolute instant (timestamptz).
 * These helpers turn a court's wall-clock time on a given date into an instant, and show an
 * instant in any viewer's zone, with daylight saving handled by the IANA database in the browser.
 *
 * Wall-clock times that daylight saving skips (spring forward) move forward by the length of the
 * gap; times that happen twice (fall back) resolve to the first occurrence.
 */
import {formatInTimeZone} from 'date-fns-tz';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;
const TIME_PATTERN = /^\d\d:\d\d(:\d\d)?$/;

/**
 * True when `name` is an exact-case IANA zone with a region (America/Chicago) or UTC that this
 * browser knows. The same policy as the Court time zone invariant (schemas/court.ts).
 */
export function isValidTimeZone(name: string | null | undefined): boolean {
    if (!name || (!name.includes('/') && name !== 'UTC')) return false;
    // Every IANA name part starts with a capital letter (America/Port-au-Prince, Etc/GMT+5), so a
    // lowercase start means wrong case even when the browser maps the name to a different link.
    if (name.split('/').some(part => !/^[A-Z]/.test(part))) return false;
    try {
        // The browser canonicalizes the case of the name. Same name apart from case means the
        // case was wrong; a different name is an IANA link such as Asia/Calcutta.
        const known = new Intl.DateTimeFormat('en-US', {timeZone: name}).resolvedOptions().timeZone;
        return known === name || known.toLowerCase() !== name.toLowerCase();
    } catch {
        return false;
    }
}

/** The viewer's zone as the browser reports it, or null when it reports none. */
export function browserTimeZone(): string | null {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(zone) ? zone : null;
}

/**
 * Every IANA zone the browser lists, sorted, for a picker. Intl.supportedValuesOf arrived in
 * Safari 15.4, Chrome 99 and Firefox 93; on older browsers the list is empty and the picker
 * offers only the zones already known to it (the stored zone and the browser's own).
 */
export function listTimeZones(): string[] {
    if (typeof Intl.supportedValuesOf !== 'function') return [];
    const zones = Intl.supportedValuesOf('timeZone');
    return zones.includes('UTC') ? zones : [...zones, 'UTC'].sort();
}

/** Picker options: the browser's zones, plus the stored zone when the browser does not list it. */
export function timeZoneOptionList(zones: string[], stored: string | null | undefined) {
    const withStored = stored && !zones.includes(stored) ? [stored, ...zones] : zones;
    return withStored.map(zone => ({value: zone, label: zone}));
}

function validCalendarDate(date: string): boolean {
    const d = new Date(`${date}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(date);
}

/** The zone's offset from UTC at an instant, in ms, read from Intl and not from the host zone. */
function zoneOffsetMs(instantMs: number, timeZone: string): number {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(new Date(instantMs));
    const n = (type: string) => Number(parts.find(p => p.type === type)!.value);
    const localAsUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'));
    return localAsUtc - Math.floor(instantMs / 1000) * 1000;
}

/**
 * The instant at which a court's wall clock reads `time` on `date`.
 * @param date calendar date at the court, yyyy-MM-dd
 * @param time wall-clock time at the court, HH:mm or HH:mm:ss
 * @param courtTimeZone the court's IANA zone
 * @throws if any argument is malformed or the zone is unknown
 */
export function courtWallTimeToInstant(date: string, time: string, courtTimeZone: string): Date {
    if (!DATE_PATTERN.test(date)) throw new Error(`Bad date "${date}", expected yyyy-MM-dd`);
    if (!TIME_PATTERN.test(time)) throw new Error(`Bad time "${time}", expected HH:mm or HH:mm:ss`);
    if (!isValidTimeZone(courtTimeZone)) throw new Error(`Unknown time zone "${courtTimeZone}"`);
    const [h, m, sec = '00'] = time.split(':');
    const wallMs = Date.parse(`${date}T${h}:${m}:${sec}Z`);
    if (Number.isNaN(wallMs) || !validCalendarDate(date)) throw new Error(`Bad date or time "${date} ${time}"`);

    // The offsets in force a day before and a day after the wall time (zone changes are further
    // apart than that). A candidate instant is kept only if the zone really has that offset then,
    // that is, if the court clock reads the wall time at that instant. Nothing here uses the
    // host's zone, so the answer is the same on every machine.
    const candidates = [wallMs - DAY_MS, wallMs + DAY_MS]
        .map(probe => zoneOffsetMs(probe - zoneOffsetMs(probe, courtTimeZone), courtTimeZone))
        .map(offset => wallMs - offset)
        .filter(instant => zoneOffsetMs(instant, courtTimeZone) === wallMs - instant);
    if (candidates.length > 0) return new Date(Math.min(...candidates));

    // A time inside a spring-forward gap never shows on the court clock. Move it forward by the
    // gap: read it with the offset before the change, so 02:30 in a 02:00 to 03:00 gap is 03:30.
    const before = zoneOffsetMs(wallMs - DAY_MS - zoneOffsetMs(wallMs - DAY_MS, courtTimeZone), courtTimeZone);
    return new Date(wallMs - before);
}

/**
 * An instant formatted in a viewer's zone (the browser's when omitted).
 * @param pattern date-fns format pattern; the default ends with the zone abbreviation
 */
export function formatInstantInZone(
    instant: Date,
    viewerTimeZone: string | null = browserTimeZone(),
    pattern: string = 'EEE MMM d, h:mm a zzz'
): string {
    if (!viewerTimeZone) throw new Error('No viewer time zone');
    if (!isValidTimeZone(viewerTimeZone)) throw new Error(`Unknown time zone "${viewerTimeZone}"`);
    return formatInTimeZone(instant, viewerTimeZone, pattern);
}
